import { useEffect, useState, useCallback } from 'react';
import api from '../api/axios';
import styles from './Table.module.css';

export default function Notifications() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [userId, setUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const loadHistory = useCallback(async (p = 1) => {
    try {
      const { data } = await api.get(`/notifications/admin/history?page=${p}&limit=20`);
      setHistory(data.notifications || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(p);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadHistory(1);
    api
      .get('/admin/users?limit=100')
      .then((r) => setUsers(r.data.users || []))
      .catch(() => {});
  }, [loadHistory]);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    setMsg('');
    try {
      const payload = { title: title.trim(), body: body.trim() };
      if (userId) payload.userId = userId;
      const { data } = await api.post('/notifications/admin/send', payload);
      const pushNote = data.fcmConfigured
        ? 'Push attempted for devices with FCM tokens.'
        : 'In-app only (set FCM_SERVER_KEY for push).';
      setMsg(`Sent to ${data.sent} user(s). ${pushNote}`);
      setTitle('');
      setBody('');
      setUserId('');
      loadHistory(1);
    } catch (err) {
      setError(err.response?.data?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className={styles.header}>
        <h2 className={styles.title}>Notifications</h2>
      </div>

      <form
        onSubmit={send}
        className={styles.form}
        style={{
          maxWidth: 560,
          background: '#fff',
          padding: 24,
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          marginBottom: 28,
        }}
      >
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Send notification</h3>
        {error && <div className={styles.formError}>{error}</div>}
        {msg && (
          <div style={{ background: '#e8f8ef', color: '#1a9e5c', padding: '10px 14px', borderRadius: 8, fontWeight: 600, fontSize: 13 }}>
            {msg}
          </div>
        )}
        <label>Title *</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={80} />
        <label>Message *</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} required maxLength={500} />
        <label>Audience</label>
        <select value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">All active users</option>
          {users.map((u) => (
            <option key={u._id} value={u._id}>
              {u.name || 'User'} — {u.email}
            </option>
          ))}
        </select>
        <div className={styles.modalActions}>
          <button type="submit" className={styles.saveBtn} disabled={sending}>
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </form>

      <h3 style={{ marginBottom: 12 }}>Recent sends ({total})</h3>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Title</th>
              <th>Message</th>
              <th>User</th>
              <th>Push</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && (
              <tr>
                <td colSpan={5} className={styles.empty}>
                  No notifications sent yet
                </td>
              </tr>
            )}
            {history.map((n) => (
              <tr key={n._id}>
                <td style={{ fontWeight: 600 }}>{n.title}</td>
                <td style={{ maxWidth: 280, fontSize: 13 }}>{n.body}</td>
                <td style={{ fontSize: 12 }}>
                  {n.broadcastId ? (
                    <span style={{ color: '#6c63ff' }}>Broadcast</span>
                  ) : (
                    <>
                      <div>{n.user?.name || '—'}</div>
                      <div style={{ color: '#aaa' }}>{n.user?.email || ''}</div>
                    </>
                  )}
                </td>
                <td style={{ fontSize: 12 }}>
                  {n.pushSent ? (
                    <span className={styles.badgeActive}>Sent</span>
                  ) : (
                    <span className={styles.badgePending}>In-app</span>
                  )}
                </td>
                <td style={{ fontSize: 12 }}>{new Date(n.createdAt).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className={styles.pagination}>
          <button type="button" className={styles.filterBtn} disabled={page <= 1} onClick={() => loadHistory(page - 1)}>
            ← Prev
          </button>
          <span>
            Page {page} of {pages}
          </span>
          <button type="button" className={styles.filterBtn} disabled={page >= pages} onClick={() => loadHistory(page + 1)}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
